import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, curSym, fmtDate, errMsg } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import Layout, { PageHeader } from "@finance/components/Layout";
import { DepositRequestsPanel } from "@finance/components/AdvanceApply";
import { exportToCsv } from "@finance/lib/exportCsv";
import { StatusBadge } from "@finance/components/StatusBadge";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { DownloadSimple, Money, Buildings, Receipt, CalendarBlank, MagnifyingGlass, Package, Wallet } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";
import { DueChip, daysUntil } from "@finance/components/DueChip";

function LodgmentPayDialog({ row, onOpenChange, onSaved }) {
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({});
  useEffect(() => {
    if (row) {
      api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => setBanks([]));
      setForm({ bank_account_id: "", amount: String(row.payable_amount || ""), date: new Date().toISOString().slice(0, 10) });
    }
  }, [row]);
  if (!row) return null;
  const save = async () => {
    if (!form.bank_account_id) { toast.error("Select the paying bank account."); return; }
    try {
      await api.post(`/asset-lodgments/${row.lodgment_id}/pay`, { bank_account_id: form.bank_account_id, amount: Number(form.amount), date: form.date });
      toast.success("Payment posted — AP settled");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="aging-lodge-pay-dialog">
        <DialogHeader><DialogTitle className="font-display">Pay Lodgment — {row.supplier_name}</DialogTitle>
          <DialogDescription>Settle this asset lodgment's Accounts Payable directly from the aging.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-sm bg-gray-50 p-3 text-sm"><span className="text-gray-500">Outstanding:</span> <b className="lx-num">TSh {fmtNum(row.payable_amount)}</b></div>
          <div><Label className="lx-label">Pay from Bank</Label>
            <Select value={form.bank_account_id || ""} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
              <SelectTrigger className="mt-1" data-testid="aging-lodge-bank"><SelectValue placeholder="Select bank account" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.currency}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="lx-label">Amount</Label><Input type="number" className="mt-1" data-testid="aging-lodge-amount" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><Label className="lx-label">Date</Label><Input type="date" className="mt-1" data-testid="aging-lodge-date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="aging-lodge-pay-submit" disabled={!form.bank_account_id || !(Number(form.amount) > 0)} onClick={save}>Post Payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open) {
      setData(null);
      api.get("/payables/recon-detail").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="recon-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Other GL reconciling items — breakdown</DialogTitle>
          <DialogDescription>Every journal posted to Trade Payables (GL 2000), grouped by source. Groups already listed on this page are marked <b>tracked</b>; the rest make up the reconciling residual.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-10 text-center text-sm text-gray-400" data-testid="recon-loading">Loading…</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-sm border border-gray-200 bg-gray-50 p-2"><div className="lx-label text-gray-400">GL 2000 Balance</div><div className="lx-num font-bold" data-testid="recon-gl2000">TSh {fmtNum(data.gl2000)}</div></div>
              <div className="rounded-sm border border-gray-200 bg-gray-50 p-2"><div className="lx-label text-gray-400">Tracked</div><div className="lx-num font-bold">TSh {fmtNum(data.tracked_net)}</div></div>
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-2"><div className="lx-label text-amber-600">Reconciling</div><div className="lx-num font-bold text-amber-700" data-testid="recon-residual">TSh {fmtNum(data.reconciling_net)}</div></div>
            </div>
            {(data.groups || []).map((g) => (
              <div key={g.source_type} className="overflow-hidden rounded-sm border border-gray-200" data-testid={`recon-group-${g.source_type}`}>
                <div className="flex items-center justify-between bg-gray-50 px-3 py-2 border-b border-gray-200">
                  <span className="font-display text-sm font-bold text-gray-900">{g.label}
                    {g.tracked ? <span className="ml-2 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">tracked</span> : <span className="ml-2 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">reconciling</span>}
                  </span>
                  <span className={`lx-num text-sm font-semibold ${g.net < 0 ? "text-red-600" : "text-gray-900"}`}>TSh {fmtNum(g.net)}</span>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="lx-label text-gray-400 border-b border-gray-100"><th className="px-3 py-1.5 text-left">Date</th><th className="px-3 py-1.5 text-left">Memo / Ref</th><th className="px-3 py-1.5 text-right">Dr</th><th className="px-3 py-1.5 text-right">Cr</th><th className="px-3 py-1.5 text-right">Net</th></tr></thead>
                  <tbody>
                    {g.journals.map((j, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="px-3 py-1.5 lx-num whitespace-nowrap">{fmtDate(j.date)}</td>
                        <td className="px-3 py-1.5 text-gray-600">{j.memo || j.source_id || "—"}</td>
                        <td className="px-3 py-1.5 text-right lx-num">{j.debit ? fmtNum(j.debit) : "—"}</td>
                        <td className="px-3 py-1.5 text-right lx-num">{j.credit ? fmtNum(j.credit) : "—"}</td>
                        <td className={`px-3 py-1.5 text-right lx-num ${j.net < 0 ? "text-red-600" : ""}`}>{fmtNum(j.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ open, onOpenChange, row, onSaved }) {
  const [banks, setBanks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open && row) {
      api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => setBanks([]));
      api.get("/currencies").then((r) => {
        const curs = r.data || [];
        setCurrencies(curs);
        const cur = row.currency || "TZS";
        const c = curs.find((x) => x.code === cur);
        const spot = cur === "TZS" ? 1 : (c?.latest_rate ? Math.round(Number(c.latest_rate) * 10000) / 10000 : "");
        setForm({ amount: row.payable_amount || "", bank_account_id: "", fx_rate: spot, note: "" });
      }).catch(() => setForm({ amount: row.payable_amount || "", bank_account_id: "", fx_rate: row.currency && row.currency !== "TZS" ? "" : 1, note: "" }));
    }
  }, [open, row]);

  if (!row) return null;
  const invCur = row.currency || "TZS";
  const bank = banks.find((b) => b.id === form.bank_account_id);
  const mismatch = bank && (bank.currency || "TZS") !== invCur;
  const needRate = invCur !== "TZS" || mismatch;
  const isExpense = row.source === "expense";
  const lockFull = isExpense && row.has_supplier === false; // no-supplier expenses must be paid in full
  const owed = Number(row.payable_amount || 0);
  const over = !lockFull && owed > 0 && Number(form.amount || 0) > owed + 0.01;

  const save = async () => {
    if (!form.bank_account_id) { toast.error("Select the paying bank account."); return; }
    if (needRate && (!form.fx_rate || Number(form.fx_rate) <= 0)) { toast.error(`Enter the FX rate (1 ${invCur} = ? TZS) so this payment converts to the base currency.`); return; }
    const body = {
      source: row.source === "expense" ? "expense" : "supplier_invoice",
      transaction_id: row.source === "expense" ? null : (row.transaction_id || null),
      expense_id: row.source === "expense" ? (row.expense_id || row.id) : null,
      amount: Number(form.amount),
      currency: invCur,
      bank_account_id: form.bank_account_id,
      fx_rate: needRate ? Number(form.fx_rate) : 1,
      note: form.note || null,
    };
    try {
      await api.post("/payment-schedule", body);
      toast.success("Pushed to Payment Schedule — awaiting authorization");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle className="font-display">Schedule Payment — {row.lx_po_no || row.supplier_name}</DialogTitle>
          <DialogDescription>Assign a paying bank so the owner can authorize this payment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-gray-600">{row.supplier_name} · <span className="lx-num font-semibold">{curSym(invCur)} {fmtNum(row.payable_amount)}</span></div>
          <div>
            <Label className="lx-label">Amount to pay ({invCur})</Label>
            <Input type="number" className="mt-1" data-testid="sched-amount" value={form.amount || ""} disabled={lockFull} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            <div className="mt-1 text-[11px] text-gray-500">{lockFull ? "Expenses without a supplier must be paid in full." : "Partial amounts are allowed."}</div>
            {over && <div className="mt-1 rounded-sm border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700" data-testid="sched-overpay-warning">This is more than what's owed ({curSym(invCur)} {fmtNum(owed)}). Reduce the amount to push it to the schedule.</div>}
          </div>
          <div>
            <Label className="lx-label">Pay from Bank</Label>
            <Select value={form.bank_account_id || ""} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
              <SelectTrigger className="mt-1" data-testid="sched-bank"><SelectValue placeholder="Select bank account" /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.currency}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {needRate && (
            <div className="rounded-sm border border-amber-200 bg-amber-50/50 p-3" data-testid="sched-fx-row">
              <Label className="lx-label text-amber-600">FX Rate — 1 {invCur} = ? TZS{mismatch ? ` (bank is ${bank.currency})` : ""}</Label>
              <div className="mt-1 flex items-center gap-3">
                <Input type="number" className="w-40" data-testid="sched-fx" value={form.fx_rate ?? ""} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} placeholder="e.g. 2600" />
                <span className="text-sm text-gray-600">≈ <b className="lx-num">TSh {fmtNum((Number(form.amount) || 0) * (Number(form.fx_rate) || 0))}</b></span>
              </div>
            </div>
          )}
          <div>
            <Label className="lx-label">Note (optional)</Label>
            <Textarea className="mt-1" rows={2} data-testid="sched-note" value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-schedule" disabled={over} onClick={save}>Push to Schedule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Payables() {
  const { user } = useAuth();
  const canSchedule = user?.is_owner || user?.can_manage_payment_schedule;
  const [rows, setRows] = useState([]);
  const [schedRow, setSchedRow] = useState(null);
  const [reconOpen, setReconOpen] = useState(false);
  const [payLodge, setPayLodge] = useState(null);

  const load = useCallback(() => { api.get("/payables").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const groups = {};
  rows.forEach((t) => {
    const key = t.supplier_name || "Unassigned Supplier";
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  });

  const cols = [
    { key: "lx_po_no", label: "Somcore PO" },
    { key: "supplier_name", label: "Supplier" },
    { key: "client_name", label: "Client" },
    { key: "currency", label: "Currency" },
    { key: "payable_amount", label: "Payable", format: (v) => fmtNum(v) },
    { key: "status", label: "Status" },
    { key: "due_date", label: "Due Date", format: fmtDate },
    { key: "eta", label: "ETA", format: fmtDate },
  ];

  const grandTotal = rows.reduce((s, t) => s + (t.payable_amount_tzs ?? t.payable_amount ?? 0), 0);
  const lodgeRows = rows.filter((t) => t.source === "asset_lodgment");
  const lodgeTotal = lodgeRows.reduce((s, t) => s + (t.payable_amount_tzs ?? t.payable_amount ?? 0), 0);
  const lodgeOverdue = lodgeRows.filter((t) => { const n = daysUntil(t.due_date || t.eta); return n !== null && n < 0; }).length;

  return (
    <Layout>
      <PageHeader
        title="Payables Due"
        subtitle={`${rows.length} transactions pending payment · TSh ${fmtNum(grandTotal)}`}
        actions={<Button variant="outline" data-testid="export-payables" onClick={() => exportToCsv("payables-due", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>}
      />
      <div className="p-8">
        <DepositRequestsPanel />
        <KpiStrip cols={4}>
          <KpiCard icon={Buildings} label="Suppliers" value={Object.keys(groups).length} testid="kpi-pay-suppliers" />
          <KpiCard icon={Receipt} label="Transactions" value={rows.length} testid="kpi-pay-txns" />
          <KpiCard icon={Money} label="Total Payable" value={`TSh ${fmtNum(grandTotal)}`} tone="accent" testid="kpi-pay-total" />
          <KpiCard icon={Package} label="Unpaid Lodgments" value={lodgeRows.length} tone={lodgeOverdue > 0 ? "danger" : "default"}
            sub={lodgeRows.length ? `TSh ${fmtNum(lodgeTotal)}${lodgeOverdue > 0 ? ` · ${lodgeOverdue} overdue` : " · on track"}` : "None outstanding"} testid="kpi-pay-lodgments" />
        </KpiStrip>
        {Object.keys(groups).length === 0 && <EmptyState icon={Money} testid="payables-empty" title="No payables due" message="Approved supplier invoices awaiting payment will appear here." />}
        <div className="space-y-6">
          {Object.entries(groups).map(([supplier, items]) => {
            const total = items.reduce((s, t) => s + (t.payable_amount_tzs ?? t.payable_amount ?? 0), 0);
            return (
              <div key={supplier} className="overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid={`payable-group-${supplier}`}>
                <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-5 py-2.5">
                  <span className="font-display font-bold text-gray-900">{supplier}</span>
                  <span className="lx-num text-sm font-medium text-[#1B4383]">TSh {fmtNum(total)}</span>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="lx-label border-b border-gray-100 text-gray-400">
                    <th className="px-5 py-2 text-left">Somcore PO</th><th className="px-5 py-2 text-left">Client</th>
                    <th className="px-5 py-2 text-left">Status</th><th className="px-5 py-2 text-left">Due</th><th className="px-5 py-2 text-left">ETA</th>
                    <th className="px-5 py-2 text-right">Payable</th><th className="px-5 py-2 text-right">Action</th>
                  </tr></thead>
                  <tbody>
                    {items.map((t) => (
                      <tr key={t.id} data-testid={`payable-row-${t.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-5 py-2 font-mono-num">{t.lx_po_no || "—"}</td>
                        <td className="px-5 py-2">{t.client_name || "—"}</td>
                        <td className="px-5 py-2"><StatusBadge status={t.status} />{(t.source === "expense" && t.efd_status !== "captured" && (t.efd_pushed || t.efd_status === "pending")) ? <span className="ml-1 rounded-sm bg-amber-50 px-1 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`efd-pending-tag-${t.id}`}>EFD pending</span> : null}</td>
                        <td className="px-5 py-2 lx-num" data-testid={`payable-due-${t.id}`}>{t.due_date ? fmtDate(t.due_date) : "—"}{t.status !== "Reconciling" && t.payable_amount > 0 && <DueChip date={t.due_date || t.eta} testid={`payable-due-chip-${t.id}`} />}</td>
                        <td className="px-5 py-2 lx-num">{fmtDate(t.eta)}</td>
                        <td className="px-5 py-2 text-right lx-num">
                          {curSym(t.currency)} {fmtNum(t.payable_amount)}
                          {t.currency && t.currency !== "TZS" && t.payable_amount_tzs != null && (
                            <div className="text-[11px] font-normal text-gray-400">≈ TSh {fmtNum(t.payable_amount_tzs)}</div>
                          )}
                        </td>
                        <td className="px-5 py-2 text-right">
                          {(t.source === "gl_reconciliation") ? (
                            <button className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline" data-testid={`pay-recon-drill-${t.id}`} onClick={() => setReconOpen(true)}><MagnifyingGlass size={13} />View breakdown</button>
                          ) : (t.source === "asset_lodgment" && t.lodgment_id) ? (
                            <button className="inline-flex items-center gap-1 rounded-sm bg-[#1B4383] px-2 py-1 text-xs font-medium text-white hover:bg-[#122E5C]" data-testid={`pay-lodgment-${t.id}`} onClick={() => setPayLodge(t)}><Wallet size={13} />Pay</button>
                          ) : (t.source === "supplier_debit_note" || t.source === "import_accrual") ? (
                            <span className="text-xs text-gray-400" data-testid={`pay-gl-item-${t.id}`}>GL item</span>
                          ) : t.in_schedule ? (
                            <span className="inline-flex items-center gap-1 rounded-sm bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700" data-testid={`in-schedule-tag-${t.id}`}><CalendarBlank size={13} />In payment schedule</span>
                          ) : (t.source === "expense" || t.transaction_id) ? (
                            canSchedule ? (
                              <button className="inline-flex items-center gap-1 rounded-sm bg-[#1B4383] px-2 py-1 text-xs font-medium text-white hover:bg-[#122E5C]" data-testid={`schedule-payment-${t.id}`} onClick={() => setSchedRow(t)}><CalendarBlank size={13} />Schedule</button>
                            ) : (
                              <span className="text-xs text-gray-400" data-testid={`sched-no-perm-${t.id}`}>Schedule (Finance)</span>
                            )
                          ) : (
                            <span className="text-xs text-gray-400" data-testid={`pay-in-allocations-${t.id}`}>Pay via Allocations</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
      <ScheduleDialog open={!!schedRow} onOpenChange={(v) => !v && setSchedRow(null)} row={schedRow} onSaved={load} />
      <LodgmentPayDialog row={payLodge} onOpenChange={(v) => !v && setPayLodge(null)} onSaved={load} />
      <ReconDialog open={reconOpen} onOpenChange={setReconOpen} />
    </Layout>
  );
}
